import React, { useState } from 'react';
import formService from '../services/formService';
import { validateContactForm, validateConsultationForm, sanitizeFormData } from '../utils/formValidation';

const FormTest = () => {
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    phone: '',
    postcode: '',
    message: ''
  });
  
  const [consultationFormData, setConsultationFormData] = useState({
    name: '',
    email: '',
    phone: '',
    postcode: '',
    message: ''
  });
  
  const [contactErrors, setContactErrors] = useState({});
  const [consultationErrors, setConsultationErrors] = useState({});
  const [contactStatus, setContactStatus] = useState(null);
  const [consultationStatus, setConsultationStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactFormData(prev => ({ ...prev, [name]: value }));
    if (contactErrors[name]) {
      setContactErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleConsultationChange = (e) => {
    const { name, value } = e.target;
    setConsultationFormData(prev => ({ ...prev, [name]: value }));
    if (consultationErrors[name]) {
      setConsultationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactErrors({});
    setIsSubmitting(true);
    setContactStatus(null);

    const validationErrors = validateContactForm(contactFormData);
    if (Object.keys(validationErrors).length > 0) {
      setContactErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const sanitizedData = sanitizeFormData(contactFormData);
      const result = await formService.submitContactForm(sanitizedData);
      setContactStatus('success');
      setContactFormData({ name: '', email: '', phone: '', postcode: '', message: '' });
    } catch (error) {
      setContactStatus('error');
      console.error('Contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    setConsultationErrors({});
    setIsSubmitting(true);
    setConsultationStatus(null);

    const validationErrors = validateConsultationForm(consultationFormData);
    if (Object.keys(validationErrors).length > 0) {
      setConsultationErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const sanitizedData = sanitizeFormData(consultationFormData);
      const result = await formService.submitConsultationForm(sanitizedData);
      setConsultationStatus('success');
      setConsultationFormData({ name: '', email: '', phone: '', postcode: '', message: '' });
    } catch (error) {
      setConsultationStatus('error');
      console.error('Consultation form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">🧪 Form Test Page</h1>
          <p className="text-gray-600 mb-2">Test both forms without sending real emails</p>
          <p className="text-sm text-gray-500">Check the browser console and server logs for detailed output</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Form Test */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4 text-blue-600">Contact Form Test</h2>
            
            {contactStatus === 'success' && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                ✅ Contact form test successful! Check console for details.
              </div>
            )}
            
            {contactStatus === 'error' && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                ❌ Contact form test failed! Check console for errors.
              </div>
            )}

            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={contactFormData.name}
                  onChange={handleContactChange}
                  className={`w-full px-3 py-2 border rounded-md ${contactErrors.name ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Your name"
                />
                {contactErrors.name && <p className="text-red-500 text-sm mt-1">{contactErrors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={contactFormData.email}
                  onChange={handleContactChange}
                  className={`w-full px-3 py-2 border rounded-md ${contactErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="your@email.com"
                />
                {contactErrors.email && <p className="text-red-500 text-sm mt-1">{contactErrors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={contactFormData.phone}
                  onChange={handleContactChange}
                  className={`w-full px-3 py-2 border rounded-md ${contactErrors.phone ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="07123 456789"
                />
                {contactErrors.phone && <p className="text-red-500 text-sm mt-1">{contactErrors.phone}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                <input
                  type="text"
                  name="postcode"
                  value={contactFormData.postcode}
                  onChange={handleContactChange}
                  className={`w-full px-3 py-2 border rounded-md ${contactErrors.postcode ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="M1 1AA"
                />
                {contactErrors.postcode && <p className="text-red-500 text-sm mt-1">{contactErrors.postcode}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  name="message"
                  rows="4"
                  value={contactFormData.message}
                  onChange={handleContactChange}
                  className={`w-full px-3 py-2 border rounded-md ${contactErrors.message ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Your message here..."
                />
                {contactErrors.message && <p className="text-red-500 text-sm mt-1">{contactErrors.message}</p>}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              >
                {isSubmitting ? 'Testing...' : 'Test Contact Form'}
              </button>
            </form>
          </div>

          {/* Consultation Form Test */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4 text-green-600">Consultation Form Test</h2>
            
            {consultationStatus === 'success' && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                ✅ Consultation form test successful! Check console for details.
              </div>
            )}
            
            {consultationStatus === 'error' && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                ❌ Consultation form test failed! Check console for errors.
              </div>
            )}

            <form onSubmit={handleConsultationSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={consultationFormData.name}
                  onChange={handleConsultationChange}
                  className={`w-full px-3 py-2 border rounded-md ${consultationErrors.name ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Your name"
                />
                {consultationErrors.name && <p className="text-red-500 text-sm mt-1">{consultationErrors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={consultationFormData.email}
                  onChange={handleConsultationChange}
                  className={`w-full px-3 py-2 border rounded-md ${consultationErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="your@email.com"
                />
                {consultationErrors.email && <p className="text-red-500 text-sm mt-1">{consultationErrors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={consultationFormData.phone}
                  onChange={handleConsultationChange}
                  className={`w-full px-3 py-2 border rounded-md ${consultationErrors.phone ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="07123 456789"
                />
                {consultationErrors.phone && <p className="text-red-500 text-sm mt-1">{consultationErrors.phone}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                <input
                  type="text"
                  name="postcode"
                  value={consultationFormData.postcode}
                  onChange={handleConsultationChange}
                  className={`w-full px-3 py-2 border rounded-md ${consultationErrors.postcode ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="M1 1AA"
                />
                {consultationErrors.postcode && <p className="text-red-500 text-sm mt-1">{consultationErrors.postcode}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  name="message"
                  rows="4"
                  value={consultationFormData.message}
                  onChange={handleConsultationChange}
                  className={`w-full px-3 py-2 border rounded-md ${consultationErrors.message ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Your consultation details..."
                />
                {consultationErrors.message && <p className="text-red-500 text-sm mt-1">{consultationErrors.message}</p>}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-400"
              >
                {isSubmitting ? 'Testing...' : 'Test Consultation Form'}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-bold text-yellow-800 mb-2">📋 Test Instructions:</h3>
          <ol className="list-decimal list-inside text-yellow-700 space-y-1">
            <li>Test forms directly through the website forms</li>
            <li>Fill out both forms with test data</li>
            <li>Check the browser console for frontend logs</li>
            <li>Check the test server console for backend logs</li>
            <li>Verify the message field is captured correctly</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default FormTest;
